import type {
  AlmanacInterestResponse,
  EventEngagement,
  IdeaInterestResponse,
  InterestRung,
  InterestSource,
  MyEventsScope,
  NativeInterestResponse,
} from '@@/types/api';
import { useApi } from './use-api';

// Unlike the single-base domain composables (commitments, informational-events),
// interest endpoints span three unrelated base paths (`/events`,
// `/informational-events`, `/my-events`), so we use an empty base and pass
// absolute paths — cleaner than juggling multiple `useApi(...)` instances.
//
// These endpoints are declaratively idempotent (PUT/DELETE by resource), so no
// Idempotency-Key header is sent — the server derives state from the target.
export function useEventInterestApi() {
  const { get, put, del } = useApi();

  return {
    // Native interest is "Interested"-only and takes NO request body. A
    // terminal/concluded event responds 422 (surfaced as an ApiError).
    setNativeInterest: (id: string) =>
      put<NativeInterestResponse>(`/api/v1/events/${id}/interest`),
    clearNativeInterest: (id: string) =>
      del<{ message: string }>(`/api/v1/events/${id}/interest`),

    // Almanac interest requires an explicit rung ("interested" | "going").
    setAlmanacInterest: (id: string, rung: InterestRung) =>
      put<AlmanacInterestResponse>(
        `/api/v1/informational-events/${id}/interest`,
        { body: { rung } },
      ),
    clearAlmanacInterest: (id: string) =>
      del<{ message: string }>(`/api/v1/informational-events/${id}/interest`),

    // Idea interest is "Interested"-only (no rung) and takes NO request body —
    // declaratively idempotent by resource, like native. Declaring "going" is
    // structurally unreachable (the route has no rung).
    setIdeaInterest: (id: string) =>
      put<IdeaInterestResponse>(`/api/v1/event-ideas/${id}/interest`),
    clearIdeaInterest: (id: string) =>
      del<{ message: string }>(`/api/v1/event-ideas/${id}/interest`),

    // `my-events` is enveloped as `{ data: [...] }` — unwrap to the bare array.
    getMyEvents: async (
      scope: MyEventsScope,
      source?: InterestSource,
    ): Promise<EventEngagement[]> => {
      const res = await get<{ data: EventEngagement[] }>('/api/v1/my-events', {
        params: { scope, ...(source ? { source } : {}) },
      });
      return res.data;
    },
  };
}
